// ===========================================
// Two-Factor Authentication Verification Component
// Phase 2 - Priority 6
// ===========================================
// Component for verifying 2FA code during login

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Link,
  Divider,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Key as KeyIcon,
  Smartphone as SmartphoneIcon,
} from '@mui/icons-material';
import { useVerify2FALogin } from '../../hooks/useTwoFactor';

// ===========================================
// TYPES
// ===========================================

interface TwoFactorVerifyProps {
  tempToken: string;
  email: string;
  onSuccess: (data: {
    accessToken: string;
    refreshToken: string;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      schoolId: string;
    };
  }) => void;
  onCancel?: () => void;
}

// ===========================================
// COMPONENT
// ===========================================

export function TwoFactorVerify({
  tempToken,
  email,
  onSuccess,
  onCancel,
}: TwoFactorVerifyProps): React.ReactElement {
  // State
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Hooks
  const verify2FA = useVerify2FALogin();

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, [useBackupCode]);

  // Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code) return;

    // Validate code length
    if (!useBackupCode && code.length !== 6) return;
    if (useBackupCode && code.length !== 8) return;

    try {
      const response = await verify2FA.mutateAsync({
        tempToken,
        code,
        trustDevice,
      });

      onSuccess(response.data);
    } catch {
      // Error handled by hook
      setCode('');
      inputRef.current?.focus();
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '');

    if (useBackupCode) {
      // Backup codes are alphanumeric, 8 characters
      setCode(value.toUpperCase().slice(0, 8));
    } else {
      // TOTP codes are numeric, 6 digits
      setCode(value.replace(/\D/g, '').slice(0, 6));
    }
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setCode('');
  };

  const isValidCode = useBackupCode ? code.length === 8 : code.length === 6;

  return (
    <Card sx={{ maxWidth: 400, mx: 'auto' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, justifyContent: 'center' }}>
          <SecurityIcon color="primary" fontSize="large" />
          <Typography variant="h6">Two-Factor Authentication</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          {useBackupCode
            ? 'Enter one of your backup codes to sign in.'
            : `Enter the 6-digit code from your authenticator app for ${email}`}
        </Typography>

        {verify2FA.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {useBackupCode
              ? 'Invalid backup code. Please check and try again.'
              : 'Invalid verification code. Please check and try again.'}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <TextField
              inputRef={inputRef}
              label={useBackupCode ? 'Backup Code' : 'Verification Code'}
              value={code}
              onChange={handleCodeChange}
              placeholder={useBackupCode ? 'ABCD1234' : '000000'}
              inputProps={{
                maxLength: useBackupCode ? 8 : 6,
                style: {
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: useBackupCode ? '0.25rem' : '0.5rem',
                  fontFamily: 'monospace',
                },
                autoComplete: 'one-time-code',
              }}
              sx={{ width: useBackupCode ? 220 : 200 }}
              autoFocus
              error={verify2FA.isError}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={trustDevice}
                  onChange={(e) => setTrustDevice(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Trust this device for 30 days
                </Typography>
              }
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {onCancel && (
              <Button onClick={onCancel} disabled={verify2FA.isPending}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={!isValidCode || verify2FA.isPending}
              startIcon={verify2FA.isPending ? <CircularProgress size={16} /> : <KeyIcon />}
            >
              {verify2FA.isPending ? 'Verifying...' : 'Verify'}
            </Button>
          </Box>
        </form>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            variant="body2"
            onClick={toggleBackupCode}
            sx={{ cursor: 'pointer' }}
          >
            {useBackupCode ? (
              <>
                <SmartphoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Use authenticator app instead
              </>
            ) : (
              'Use a backup code instead'
            )}
          </Link>
        </Box>

        {verify2FA.data?.data.backupCodeUsed && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Backup code used. You have {verify2FA.data.data.remainingBackupCodes} codes remaining.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

export default TwoFactorVerify;
